import React, { useMemo, useState } from "react";
import {
    Button,
    CircularProgress,
    Menu,
    MenuItem,
    Typography,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import FulfillmentGrid from "../components/Shipping/FulfillmentGrid.jsx";
import PullDateRangeFields from "../components/Helpers/PullDateRangeFields.jsx";
import useApiData from "../services/useApiData.js";
import PostData from "../services/PostData.js";
import { catalogQuietButtonSx } from "../components/Products/catalogGridStyles.js";
import "../components/Products/catalog.css";

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultPullRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 3);
    return { startDate: dayInputValue(start), endDate: dayInputValue(end) };
}

function pullRangeError(startDate, endDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        return "Choose a start and end date.";
    }
    if (startDate > endDate) return "Start date must be on or before the end date.";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (days > 62) return "Date range cannot be longer than 62 days.";
    return "";
}

export default function Fulfillment() {
    const { data, error, isLoading, load, refetch } = useApiData("api/broylesllc/fulfillment");
    const rows = Array.isArray(data) ? data : [];

    const initialRange = useMemo(() => defaultPullRange(), []);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState("");
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const rangeError = pullRangeError(startDate, endDate);

    const pullOrders = async (action) => {
        if (rangeError) return;
        setMenuAnchor(null);
        setPulling(true);
        setPullError("");
        try {
            const response = await PostData(
                { action, startDate, endDate, syncStatuses: true },
                "broylesllc/shipping/get-orders",
                null
            );
            if (!response?.success) {
                const message = response?.error?.response?.data?.message;
                setPullError(message || "Could not pull orders for that date range.");
                return;
            }
            await refetch?.();
        } finally {
            setPulling(false);
        }
    };

    return (
        <CatalogPageShell
            title="Fulfillment"
            subtitle="Pull orders, pick assigned units from the shelf, mark them picked, then finish labels in Shipping."
        >
            {error ? (
                <p style={{ color: "#b91c1c", padding: "8px 16px", margin: 0 }}>
                    Failed to load fulfillment items.
                </p>
            ) : null}
            <div className="app-page-stack">
                <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                    <div className="catalog-toolbar-spacer" />
                    {pullError ? (
                        <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                            {pullError}
                        </Typography>
                    ) : (
                        <span className="catalog-selection-hint">
                            Manage new orders here before Shipping
                        </span>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={pulling}
                        startIcon={
                            pulling ? (
                                <CircularProgress size={14} />
                            ) : (
                                <SyncOutlinedIcon fontSize="small" />
                            )
                        }
                        endIcon={<KeyboardArrowDownOutlinedIcon fontSize="small" />}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        sx={catalogQuietButtonSx}
                    >
                        Pull orders
                    </Button>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        slotProps={{ paper: { sx: { minWidth: 300, px: 0.5, py: 0.5 } } }}
                    >
                        <PullDateRangeFields
                            startDate={startDate}
                            endDate={endDate}
                            onStartChange={setStartDate}
                            onEndChange={setEndDate}
                            error={rangeError}
                            hint="Pulls orders, assigns oldest Listed units, then refreshes this list."
                        />
                        <MenuItem
                            disabled={Boolean(rangeError) || pulling}
                            onClick={() => pullOrders("all")}
                        >
                            All channels
                        </MenuItem>
                        <MenuItem
                            disabled={Boolean(rangeError) || pulling}
                            onClick={() => pullOrders("ebay")}
                        >
                            eBay
                        </MenuItem>
                        <MenuItem
                            disabled={Boolean(rangeError) || pulling}
                            onClick={() => pullOrders("walmart")}
                        >
                            Walmart
                        </MenuItem>
                    </Menu>
                </div>
                <FulfillmentGrid
                    rows={rows}
                    loading={Boolean(load || isLoading || pulling)}
                    onChanged={refetch}
                />
            </div>
        </CatalogPageShell>
    );
}
